/**
 * Dias sem expediente da unidade (Configurações > Calendário) para as contas
 * de dias úteis feitas no navegador.
 *
 * Uma request por usuário a cada 60 s, num cache de módulo compartilhado
 * pelas telas (mesmo desenho de `useProjetistasExecutivo`). O intervalo lido
 * cobre o que a Capacidade da Fábrica precisa, em duas consultas (o backend
 * aceita até 800 dias por consulta):
 * - a recente: o ritmo do período passado mais antigo exibido (6 meses + 28
 *   semanas para trás) e o horizonte da fila (5 meses para a frente);
 * - a do fluxo: o prazo venda → carregamento mede ambientes carregados nas
 *   últimas 52 semanas, e a VENDA deles pode ser bem anterior. Sem os
 *   feriados dessa época, eles contariam como dia útil e o prazo sairia
 *   maior do que foi.
 *
 * `anosComRegistro` vem da unidade inteira (não só do intervalo): é ele que
 * diz se um ano tem calendário cadastrado. Ano sem nenhum registro bloqueia a
 * capacidade daquele ano em vez de contar feriado como dia de trabalho.
 */
import { useCallback, useEffect, useState } from "react";

import { useAuth } from "../context/AuthContext";
import { listarDiasNaoUteis } from "../services/calendario";
import { hojeIso, somarDiasCorridos } from "../utils/capacidade/calendario";

const VALIDADE_MS = 60_000;
export const DIAS_ANTES_DE_HOJE = 420;
export const DIAS_DEPOIS_DE_HOJE = 365;
/** 52 semanas de carregamentos + até 2 anos de venda → carregamento antes deles. */
export const DIAS_ANTES_DE_HOJE_DO_FLUXO = 52 * 7 + 730;

const VAZIO = Object.freeze({
  naoUteis: new Set(),
  anosComRegistro: new Set(),
  podeEditar: false,
  carregado: false,
  erro: null,
});

const cache = { dono: null, estado: VAZIO, lidoEm: 0, emAndamento: null };
const ouvintes = new Set();

function publicar(estado) {
  cache.estado = estado;
  ouvintes.forEach((ouvinte) => ouvinte(estado));
}

/**
 * Intervalos pedidos ao backend, relativos a hoje, sem buraco nem sobreposição:
 * `[do fluxo, recente]` (ver o topo do arquivo).
 */
export function intervalosDoCalendario(hoje = hojeIso()) {
  return [
    {
      de: somarDiasCorridos(hoje, -DIAS_ANTES_DE_HOJE_DO_FLUXO),
      ate: somarDiasCorridos(hoje, -DIAS_ANTES_DE_HOJE - 1),
    },
    {
      de: somarDiasCorridos(hoje, -DIAS_ANTES_DE_HOJE),
      ate: somarDiasCorridos(hoje, DIAS_DEPOIS_DE_HOJE),
    },
  ];
}

const listaDe = (valor) => (Array.isArray(valor) ? valor : []);

/** Junta as respostas: dias de todas; `anos_com_registro` é da unidade inteira (igual em todas). */
function unirRespostas(respostas) {
  return {
    naoUteis: new Set(respostas.flatMap((r) => listaDe(r?.dias).map((dia) => dia.data)).filter(Boolean)),
    anosComRegistro: new Set(respostas.flatMap((r) => listaDe(r?.anos_com_registro)).map(Number)),
    podeEditar: respostas.some((r) => Boolean(r?.pode_editar)),
  };
}

function buscar(request, dono) {
  if (cache.emAndamento && cache.dono === dono) return cache.emAndamento;
  cache.dono = dono;
  cache.emAndamento = Promise.all(intervalosDoCalendario().map((intervalo) => listarDiasNaoUteis(request, intervalo)))
    .then((respostas) => {
      if (cache.dono !== dono) return;
      cache.lidoEm = Date.now();
      publicar({ ...unirRespostas(respostas), carregado: true, erro: null });
    })
    // Sem o calendário a conta de dias úteis estaria errada: a tela mostra o
    // erro em vez de calcular. Próxima tentativa só depois da validade.
    .catch((erro) => {
      if (cache.dono !== dono) return;
      cache.lidoEm = Date.now();
      publicar({ ...VAZIO, carregado: true, erro: erro?.message || "Não foi possível ler o calendário." });
    })
    .finally(() => {
      cache.emAndamento = null;
    });
  return cache.emAndamento;
}

/**
 * Força a próxima montagem a reler (chamado depois de editar o calendário em
 * Configurações, para a Capacidade não ficar 60 s com a lista velha).
 */
export function invalidarCalendarioUnidade() {
  cache.lidoEm = 0;
}

/** Só para testes: esquece o que foi lido. */
export function limparCacheDoCalendario() {
  cache.dono = null;
  cache.estado = VAZIO;
  cache.lidoEm = 0;
  cache.emAndamento = null;
}

/**
 * `{naoUteis: Set<"AAAA-MM-DD">, anosComRegistro: Set<number>, podeEditar,
 * carregado, erro, recarregar}`. `carregado` = a leitura terminou (com
 * sucesso ou com `erro`).
 */
export default function useCalendarioUnidade() {
  const { request, usuario } = useAuth() || {};
  const dono = usuario?.id ?? null;
  const [estado, setEstado] = useState(() => (cache.dono === dono ? cache.estado : VAZIO));

  useEffect(() => {
    if (!request) return undefined;
    ouvintes.add(setEstado);
    if (cache.dono !== dono) {
      cache.estado = VAZIO;
      cache.lidoEm = 0;
    }
    setEstado(cache.estado);
    if (Date.now() - cache.lidoEm > VALIDADE_MS) buscar(request, dono);
    return () => {
      ouvintes.delete(setEstado);
    };
  }, [request, dono]);

  const recarregar = useCallback(() => {
    cache.lidoEm = 0;
    return buscar(request, dono);
  }, [request, dono]);

  return { ...estado, recarregar };
}
